import { push } from 'connected-react-router';
import { isEmpty, isNil } from 'lodash';
import { toast } from 'react-toastify';
import { formValueSelector } from 'redux-form/immutable';
import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import * as Sentry from '@sentry/react';
import PATHS from '../../constants/paths';
import { StorageNames } from '../../constants/storageNames';
import { IAction } from '../../interfaces/IAction';
import { ENDPOINTS } from '../../modules/api/endpoints';
import { createApiRequestSaga } from '../../modules/api/saga';
import { closeModal } from '../../modules/modal/actions';
import { setUserIdForErrors } from '../../utils/ErrorLogger';
import { authSignInSuccess, authSignUpSuccess } from '../Auth/actions';
import { deleteAllPages } from '../PagesHistory/actions';
import {
  cancelSubscription,
  changeUserPassword,
  changeUserWordsGoal,
  clickUserProfileDeleteAllPagesButton,
  deleteAccount,
  deleteAccountClick,
  deleteAllPagesData,
  loadUserProfile,
  loadUserProfileError,
  reloadUserProfile,
  submitChangeUserPasswordForm,
  submitChangeWordsGoalForm,
  syncUserProfile,
  updateUserProfileFromExternalSource,
  updateUserProfileInfo,
  updateUserSettings,
  cancelGumroadSubscription,
  syncUserProfileWithServer,
  clickUserProfileLogout,
} from './actions';
import { CHANGE_PASSWORD_MODAL_NAME } from './ChangePasswordModal';
import { CHANGE_WORDS_GOAL_MODAL_NAME } from './ChangeWordsGoalModal';
import { initialState } from './reducer';
import { getUserProfile } from './selectors';
import { getAuthToken } from '../Auth/selectors';
import { appGoOnline } from '../App/actions';
import _ from 'lodash';
import { trackEvent } from '../../utils/Analytics';

function* loadProfileSaga() {
  try {
    const rawUserProfile = localStorage.getItem(StorageNames.userProfile);
    if (!rawUserProfile) {
      return;
    }

    let userProfileState = JSON.parse(rawUserProfile);

    if (isNil(userProfileState) || isEmpty(userProfileState)) {
      userProfileState = initialState;
    }

    userProfileState.notices = userProfileState.notices || {};
    userProfileState.badges = userProfileState.badges || [];

    yield put(updateUserProfileFromExternalSource(userProfileState));

    localStorage.removeItem(StorageNames.userProfile);
    localStorage.setItem(`Legacy:${StorageNames.userProfile}`, rawUserProfile);

    if (userProfileState.userInfo) {
      setUserIdForErrors({
        id: userProfileState.userInfo.id,
        email: userProfileState.userInfo.email,
      });

      yield reloadUserProfileFromServer();
    }
  } catch (ex) {
    yield put(loadUserProfileError(ex.message));
  }
}

function* syncUserProfileWithServerSaga() {
  const token = yield select(getAuthToken);

  if (token) {
    yield put(
      syncUserProfile.request({
        url: ENDPOINTS.USER_PROFILE.INDEX,
        method: 'GET',
      }),
    );
  }
}

const MAX_PROFILE_RELOAD_RETRIES = 5;

function* reloadUserProfileFromServer() {
  let retries = 0;
  while (retries < MAX_PROFILE_RELOAD_RETRIES) {
    retries++;

    yield put(syncUserProfileWithServer());

    const userProfile = yield select(getUserProfile);

    const isSubscribed = userProfile.userInfo && userProfile.userInfo.subscription.isSubscribed;

    if (isSubscribed) {
      break;
    }

    yield delay(0.5 * retries);
  }
}

function* signUpSignInWatcher(action: ReturnType<typeof authSignInSuccess>) {
  yield put(updateUserProfileInfo(action.payload));
  setUserIdForErrors({
    id: `${action.payload.id}`,
    email: action.payload.email,
  });
}

const deleteAllPagesSaga = createApiRequestSaga({
  actionSuccess: deleteAllPagesData.success,
  actionError: deleteAllPagesData.failure,
});

const deleteAccountSaga = createApiRequestSaga({
  actionSuccess: deleteAccount.success,
  actionError: deleteAccount.failure,
});

const changePasswordSaga = createApiRequestSaga({
  actionSuccess: changeUserPassword.success,
  actionError: changeUserPassword.failure,
});

const changeWordsGoalSaga = createApiRequestSaga({
  actionSuccess: changeUserWordsGoal.success,
  actionError: changeUserWordsGoal.failure,
});

const syncUserProfileSaga = createApiRequestSaga({
  actionSuccess: syncUserProfile.success,
  actionError: syncUserProfile.failure,
});

function* deleteAllDataWatcher({ payload: toastMessage }: IAction) {
  yield put(
    deleteAllPagesData.request({
      url: ENDPOINTS.PAGES.ALL,
      method: 'DELETE',
    }),
  );

  trackEvent('Delete_all_data', {});

  yield take(getType(deleteAllPagesData.success));
  yield put(deleteAllPages());
  yield call(toast, toastMessage);
}

function* deleteAccountWatcher({ payload: toastMessage }: IAction) {
  yield put(
    deleteAccount.request({
      url: ENDPOINTS.USER_PROFILE.INDEX,
      method: 'DELETE',
    }),
  );

  trackEvent('Delete_account', {});

  yield take(getType(deleteAccount.success));

  yield put(clickUserProfileLogout());
  yield put(push(PATHS.AUTH.SIGN_UP));
  yield call(toast, toastMessage);
}

function* changePasswordWatcher({ payload: toastMessage }: IAction) {
  Sentry.captureMessage('Change password');
  const data = yield select(formValueSelector(CHANGE_PASSWORD_MODAL_NAME), 'oldPassword', 'newPassword');

  yield put(
    changeUserPassword.request({
      url: ENDPOINTS.USER_PROFILE.PASSWORD,
      method: 'PUT',
      data,
    }),
  );

  yield take(getType(changeUserPassword.success));
  yield call(toast, toastMessage);
  yield put(closeModal(CHANGE_PASSWORD_MODAL_NAME));
}

function* changeWordsGoalWatcher(action: any) {
  const wordsGoal = yield select(formValueSelector(CHANGE_WORDS_GOAL_MODAL_NAME), 'wordsGoal');

  trackEvent('Change_words_goal', {
    words_goal: wordsGoal,
  });

  yield put(
    changeUserWordsGoal.request({
      url: ENDPOINTS.USER_PROFILE.SETTINGS,
      method: 'PUT',
      data: {
        wordsGoal,
      },
    }),
  );

  const successAction = yield take(getType(changeUserWordsGoal.success));

  yield put(updateUserSettings(successAction.payload));

  yield call(toast, action.payload);
  yield put(closeModal(CHANGE_WORDS_GOAL_MODAL_NAME));
}

function* onUserProfileSync(action: ReturnType<typeof syncUserProfile.success>) {
  const existingUserProfile = yield select(getUserProfile);

  const newUserProfile = action.payload;

  if (_.isEqual(existingUserProfile, newUserProfile)) {
    yield put(updateUserProfileInfo(newUserProfile));
  }
}

const cancelGumroadSubscriptionSaga = createApiRequestSaga({
  actionSuccess: cancelGumroadSubscription.success,
  actionError: cancelGumroadSubscription.failure,
});

function* cancelSubscriptionWatcher() {
  yield put(
    cancelGumroadSubscription.request({
      url: ENDPOINTS.SUBSCRIPTION.CANCEL,
      method: 'POST',
    }),
  );

  yield take(getType(cancelGumroadSubscription.success));

  yield reloadUserProfileFromServer();

  trackEvent('Cancel_subscription', {});

  yield call(toast, 'Subscription canceled');
}

export default function* userProfileSaga() {
  yield takeEvery([getType(authSignInSuccess), getType(authSignUpSuccess)], signUpSignInWatcher);
  yield takeEvery([getType(loadUserProfile)], loadProfileSaga);
  yield takeEvery([getType(deleteAllPagesData.request)], deleteAllPagesSaga);
  yield takeEvery([getType(clickUserProfileDeleteAllPagesButton)], deleteAllDataWatcher);
  yield takeEvery([getType(deleteAccountClick)], deleteAccountWatcher);
  yield takeEvery([getType(deleteAccount.request)], deleteAccountSaga);

  yield takeEvery([getType(syncUserProfile.request)], syncUserProfileSaga);
  yield takeEvery([getType(reloadUserProfile)], reloadUserProfileFromServer);
  yield takeEvery([getType(syncUserProfile.success)], onUserProfileSync);
  yield takeEvery([getType(changeUserPassword.request)], changePasswordSaga);
  yield takeEvery([getType(changeUserWordsGoal.request)], changeWordsGoalSaga);
  yield takeEvery([getType(submitChangeUserPasswordForm)], changePasswordWatcher);
  yield takeEvery([getType(submitChangeWordsGoalForm)], changeWordsGoalWatcher);
  yield takeEvery([getType(cancelSubscription)], cancelSubscriptionWatcher);
  yield takeEvery([getType(cancelGumroadSubscription.request)], cancelGumroadSubscriptionSaga);
  yield takeEvery([getType(syncUserProfileWithServer), getType(appGoOnline)], syncUserProfileWithServerSaga);
}



// WEBPACK FOOTER //
// ./src/containers/UserProfile/saga.ts